import React, { useEffect, useRef } from 'react';

import IconButton from '@material-ui/core/IconButton';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import { type ClassMap, makeMuiStyles } from '@styles';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.middleText,
    ...common.flexInline,
    alignItems: 'center',
    color: theme.palette.text.primary,
    padding: '0.5rem 1rem',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 5,
    '&$success': {
      backgroundColor: '#F2F7F9',
      borderColor: theme.palette.primary.main,
    },
    '&$error': {
      backgroundColor: '#FFF1F0',
      borderColor: '#FFA39E',
    },
    '&$warning': {
      backgroundColor: '#FFFBE6',
      borderColor: '#FFBC38',
    },
    '& a': {
      ...common.link,
      textDecoration: 'underline',
    },
  },
  alertDetailsContainer: {
    borderRadius: 6,
    ...common.flexColumn,
    ...common.itemsStretch,
    ...common.textBlack,
    padding: '1rem 1rem',
  },
  alertDetailsTitle: {
    ...common.normalText,
    ...common.fontWeight600Important,
    marginBottom: '0.5rem',
  },
  icon: {
    height: 18,
    width: 18,
    marginRight: 8,
    '$success &': {
      color: theme.palette.primary.main,
    },
    '$error &': {
      color: '#FF2530',
    },
    '$warning &': {
      color: '#FFBC38',
    },
  },
  marginTop: {
    marginTop: '1rem',
  },
  gutter: {
    marginBottom: '1rem',
  },
  success: {},
  error: {},
  warning: {},
}));

export type AlertSeverity = 'success' | 'error' | 'warning';

export interface AlertProps {
  severity: AlertSeverity;
  open: boolean;
  scrollOnOpen?: boolean;
  scrollToCenter?: boolean;
  onClose?: () => void;
  classes?: ClassMap<'container'>;
  dataId?: string;
  marginTop?: boolean;
  gutter?: boolean;
  hideIcon?: boolean;
  scrollTrigger?: number;
}

export const SeverityIcon: React.FC<{ severity: AlertSeverity }> = React.memo(({ severity }) => {
  const classes = useStyles();

  return {
    success: <CheckCircleIcon className={classes.icon} />,
    error: <CancelIcon className={classes.icon} />,
    warning: <InfoIcon className={classes.icon} />,
  }[severity];
});

export const Alert: React.FC<React.PropsWithChildren<AlertProps>> = (props) => {
  const classes = useStyles();

  const ref = useRef<HTMLDivElement>(null);

  const {
    severity,
    children,
    open,
    scrollOnOpen = true,
    scrollToCenter = true,
    onClose,
    classes: customerClasses = {},
    dataId,
    gutter,
    marginTop,
    hideIcon,
    scrollTrigger,
  } = props;

  useEffect(() => {
    if (scrollOnOpen && open) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: scrollToCenter ? 'center' : 'start',
        });
      }, 100);
    }
  }, [scrollOnOpen, open, scrollTrigger]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className={clsx(
        classes.container,
        classes[severity],
        customerClasses.container,
        marginTop && classes.marginTop,
        gutter && classes.gutter,
      )}
      role="alert"
      data-severity={severity}
    >
      {!hideIcon && <SeverityIcon severity={severity} />}
      <div
        data-testid={dataId}
        style={{
          flex: 1,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: 3,
        }}
      >
        {children}
      </div>
      {onClose && (
        <IconButton onClick={onClose} aria-label="close" style={{ padding: 0 }}>
          <CloseIcon />
        </IconButton>
      )}
    </div>
  );
};

export interface AlertDetailsProps {
  severity: AlertSeverity;
  open: boolean;
  scrollOnOpen?: boolean;
  scrollToCenter?: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  marginTop?: boolean;
  gutter?: boolean;
  classes?: ClassMap<'container'>;
  hideIcon?: boolean;
}

export const AlertDetails: React.FC<AlertDetailsProps> = (props) => {
  const classes = useStyles();

  const ref = useRef<HTMLDivElement>(null);

  const {
    severity,
    title,
    open,
    scrollOnOpen = true,
    scrollToCenter = false,
    marginTop = false,
    gutter = false,
    children,
    classes: customClasses,
  } = props;

  useEffect(() => {
    if (scrollOnOpen && open) {
      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: scrollToCenter ? 'center' : 'start',
        });
      }, 100);
    }
  }, [scrollOnOpen, open]);

  return (
    <div
      ref={ref}
      className={clsx(
        classes.container,
        classes[severity],
        classes.alertDetailsContainer,
        marginTop && classes.marginTop,
        gutter && classes.gutter,
        customClasses?.container,
      )}
    >
      {title && (
        <div className={classes.alertDetailsTitle}>
          <span style={{ position: 'relative', top: 3 }}>
            <SeverityIcon severity={severity} />
          </span>
          {title}
        </div>
      )}
      <div>{children}</div>
    </div>
  );
};
